import { useEffect, useRef, useState } from 'react'
import { LessonReadyRow, LessonOptionsDropdown } from 'lesson-builder/components/lesson-status'
import styles from 'css/components/lesson-builder/lesson-builder-header.module.css'

const GRADE_BANDS = ['K-2', '3-5', '6-8', '9-12']
const SUBJECTS = ['ELA', 'Math', 'Science', 'Social Studies', 'Other']

/* Mirrors the mockup's edit-meta popover (AllForOne_LessonGenerator.html):
   an Edit chip next to the read-only grade band / subject values opens a
   small popover with both as selects, plus Cancel/Save. */
const EditMetaPopover: React.FC<{
  gradeBand?: string
  subject?: string
  onSave: (patch: { gradeBand?: string; subject?: string }) => void
}> = ({ gradeBand, subject, onSave }) => {
  const [open, setOpen] = useState(false)
  const [draftGradeBand, setDraftGradeBand] = useState(gradeBand || GRADE_BANDS[0])
  const [draftSubject, setDraftSubject] = useState(subject || SUBJECTS[0])
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (open && wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('click', onClickOutside)
    return () => document.removeEventListener('click', onClickOutside)
  }, [open])

  const handleToggle = () => {
    if (!open) {
      setDraftGradeBand(gradeBand || GRADE_BANDS[0])
      setDraftSubject(subject || SUBJECTS[0])
    }
    setOpen(!open)
  }

  const handleSave = () => {
    const patch: { gradeBand?: string; subject?: string } = {}
    if (draftGradeBand !== gradeBand) patch.gradeBand = draftGradeBand
    if (draftSubject !== subject) patch.subject = draftSubject
    if (patch.gradeBand || patch.subject) onSave(patch)
    setOpen(false)
  }

  return (
    <div className={styles.editMetaWrap} ref={wrapRef}>
      <button type="button" className={styles.editButton} onClick={handleToggle}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
        Edit
      </button>
      {open && (
        <div className={styles.editMetaPopover}>
          <div className={styles.editMetaField}>
            <label>Grade band</label>
            <select value={draftGradeBand} onChange={(e) => setDraftGradeBand(e.target.value)}>
              {GRADE_BANDS.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div className={styles.editMetaField}>
            <label>Subject</label>
            <select value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)}>
              {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className={styles.editMetaActions}>
            <button type="button" className={styles.editMetaCancel} onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className={styles.editMetaSave} onClick={handleSave}>Save</button>
          </div>
        </div>
      )}
    </div>
  )
}

interface LessonBuilderHeaderProps {
  unapprovedCount: number
  allApproved: boolean
  lessonName: string
  setShowAssignClassOverlay: (show: boolean) => void
  isClassAssigned: boolean
  assignedClassName?: string
  gradeBand?: string
  subject?: string
  onUpdateMeta?: (patch: { gradeBand?: string; subject?: string }) => void
  hasResults?: boolean
  onLaunchLesson: () => void
  onShowStudentLogins: () => void
  onCopyToClass: () => void
  onReassign: () => void
  onPrintDraft: () => void
  onPrintAnswerKey: () => void
  onPrintWorksheets: () => void
  teachStatus: string
  isRenaming?: boolean
  renamingValue?: string
  onStartRename?: () => void
  onRenamingValueChange?: (value: string) => void
  onConfirmRename?: () => void
  onCancelRename?: () => void
}

export const LessonBuilderHeader: React.FC<LessonBuilderHeaderProps> = ({
  allApproved,
  unapprovedCount,
  lessonName,
  teachStatus,
  isClassAssigned,
  assignedClassName,
  gradeBand,
  subject,
  onUpdateMeta,
  hasResults,
  setShowAssignClassOverlay,
  onLaunchLesson,
  onShowStudentLogins,
  onCopyToClass,
  onReassign,
  onPrintDraft,
  onPrintAnswerKey,
  onPrintWorksheets,
  isRenaming,
  renamingValue,
  onStartRename,
  onRenamingValueChange,
  onConfirmRename,
  onCancelRename
}) => {

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onConfirmRename?.()
    } else if (e.key === 'Escape') {
      onCancelRename?.()
    }
  }

  /* Layout follows AllForOne_LessonGenerator.html's `.header-top-row`: the
     title block flush left with Lesson Options pushed to the right edge of the
     same row, then the meta row, then the status row. Ours centred the title
     and the meta row and dropped Lesson Options into a row of its own below,
     which put the three blocks on three different axes. The existing <header>
     was already a space-between flex row, so the title block moves into it
     rather than a new wrapper being introduced. */
  const titleBlock = (
    <div className={styles.headerTitleBlock}>
      {isRenaming ? (
        <div className={styles.renameContainer}>
          <input
            type="text"
            value={renamingValue}
            onChange={(e) => onRenamingValueChange?.(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={onConfirmRename}
            className={styles.renameInput}
            autoFocus
          />
        </div>
      ) : (
        <h1 className={styles.lessonTitle}>
          {lessonName}
          {onStartRename && (
            <button className={styles.renameLink} onClick={onStartRename}>
              {/* Her chip carries a 12x12 pencil, inlined so it takes the
                  chip's own colour rule rather than shipping another asset --
                  same reasoning as the footer's social icons. This was the
                  entire 15px width gap against the mockup; every other value
                  on the chip already matched. */}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
              </svg>
              Rename
            </button>
          )}
        </h1>
      )}
      <div className="lesson-info">
        {assignedClassName && (
          <div className="class-info-line">
            Class: {assignedClassName}
            {gradeBand && <span className="grade-band-info"> (Grades {gradeBand})</span>}
            {(isClassAssigned || !!assignedClassName) && !hasResults && (
              <button className={styles.reassignButton} onClick={onReassign}>Reassign</button>
            )}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <>
      <header>
        {titleBlock}
        <LessonOptionsDropdown
          isClassAssigned={isClassAssigned}
          assignedClassName={assignedClassName}
          lessonStatus={teachStatus}
          onShowStudentLogins={onShowStudentLogins}
          onCopyToClass={onCopyToClass}
          onPrintDraft={onPrintDraft}
          onPrintAnswerKey={onPrintAnswerKey}
          onPrintWorksheets={onPrintWorksheets}
        />
      </header>
      {(gradeBand || subject) && onUpdateMeta && (
        <div className={styles.metaRow}>
          {gradeBand && (
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>Grade band</span>
              <span className={styles.metaValue}>{gradeBand}</span>
            </span>
          )}
          {gradeBand && subject && <span className={styles.metaDivider}>&middot;</span>}
          {subject && (
            <span className={styles.metaItem}>
              <span className={styles.metaLabel}>Subject</span>
              <span className={styles.metaValue}>{subject}</span>
            </span>
          )}
          <EditMetaPopover gradeBand={gradeBand} subject={subject} onSave={onUpdateMeta} />
        </div>
      )}
      <LessonReadyRow
        allApproved={allApproved}
        unapprovedCount={unapprovedCount}
        teachStatus={teachStatus}
        isClassAssigned={isClassAssigned}
        assignedClassName={assignedClassName}
        onAssignClass={() => setShowAssignClassOverlay(true)}
        onLaunchLesson={onLaunchLesson}
      />
    </>
  )
}
